<template>
  <div>
    <v-data-table
      :key="tableSelectKey"
      :headers="headers"
      :items="localFarms"
      hide-default-footer
      :server-items-length="totalFarms"
      item-key="id"
      @item-selected="handleSelect"
      @toggle-select-all="handleSelectAll"
      :show-select="true"
      mobile-breakpoint="960"
    >
      <template v-slot:top="{}">
        <v-container fluid class="pa-2">
          <!-- Filters Row -->
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-text-field
                prepend-inner-icon="mdi-magnify"
                outlined
                :placeholder="$t('search')"
                dense
                v-model="search"
                @input="handleSearchByPharse"
                clearable
                hide-details
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2">
              <v-autocomplete
                outlined
                dense
                v-model="options.country"
                :items="countries"
                item-value="name"
                item-text="name"
                :placeholder="$t('deforestation.allCountries')"
                return-object
                @change="getState"
                clearable
                hide-details
                v-on:click:clear="handleClearCountry"
              ></v-autocomplete>
            </v-col>

            <v-col v-if="toggleFilterState" cols="12" sm="6" md="4" lg="2">
              <v-autocomplete
                outlined
                dense
                v-model="options.state"
                :items="states"
                :placeholder="$t('provinceState')"
                return-object
                @change="getStateData"
                hide-details
                v-on:click:clear="handleClearState"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2">
              <v-menu
                v-model="filterByDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    append-icon="mdi-calendar"
                    v-model="dateRangeText"
                    outlined
                    dense
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    hide-details
                    v-on:click:clear="handleClearDate"
                    v-on:click:append="filterByDateMenu = true"
                    :placeholder="$t('oma.filterByDate')"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="options.date"
                  range
                  @change="handleChangeDate"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2">
              <v-autocomplete
                outlined
                dense
                v-model="selectedSociety"
                :items="societyList"
                item-value="id"
                item-text="name"
                :placeholder="$t('farm.factory')"
                return-object
                clearable
                hide-details
                @change="handleSocietyChange"
                v-on:click:clear="handleClearSociety"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2">
              <v-select
                outlined
                dense
                v-model="options.farm_created_from"
                :items="farmCreatedFromOptions"
                item-value="value"
                item-text="text"
                :placeholder="$t('farm.farmAddedBy')"
                clearable
                hide-details
                @change="handleFarmCreatedFromChange"
                v-on:click:clear="handleClearFarmCreatedFrom"
                multiple
              ></v-select>
            </v-col>
          </v-row>

          <!-- Actions Row -->
          <v-row dense align="center" class="mt-1">
            <v-col cols="12" sm="auto">
              <v-btn
                color="primary"
                height="40"
                class="px-4"
                outlined
                @click="openExportDialog"
                small
              >
                <v-img width="15px" src="/icons/pngfile.png"></v-img>
                <span class="ml-2">{{ $t("farm.exportAsCSV") }}</span>
              </v-btn>
            </v-col>
            <v-col cols="12" sm="auto">
              <v-btn
                color="red"
                height="40"
                class="px-4"
                outlined
                @click="bulkDelete"
                small
              >
                <v-icon color="red" size="20">mdi-delete</v-icon>
                <span class="ml-2">{{ $t("Bulk Delete") }}</span>
              </v-btn>
            </v-col>

            <v-spacer></v-spacer>

            <v-col cols="auto" class="d-flex align-center">
              <span class="text-body-2 grey--text text--darken-1 mr-2">
                {{ from }} – {{ to }} {{ $t("of") }} {{ totalFarms }}
              </span>
              <v-pagination
                v-model="options.page"
                :length="totalPages"
                :total-visible="7"
                color="primary"
                @input="handlePageChange"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-container>
      </template>
      <!-- Farm Name & ID: two-line cell -->
      <template v-slot:item.farmNameId="{ item }">
        <div
          class="text-decoration-underline cursor-pointer primary--text font-weight-medium"
          @click="viewFarm(item.id, 0)"
        >
          {{ item.farmName || "N/A" }}
        </div>
        <div class="text-caption grey--text text--darken-1 mt-1">
          {{ item.registrationNo || item.dimitraFarmId || "N/A" }}
        </div>
      </template>

      <!-- Producer / Farmer -->
      <template v-slot:item.farmerName="{ item }">
        <div
          class="text-decoration-underline cursor-pointer primary--text"
          @click="item.user && $router.push({ name: 'farmers_view', params: { id: item.user.id } })"
        >
          {{
            item.user
              ? item.user.firstName +
                (item.user.middleName ? " " + item.user.middleName : "") +
                (item.user.lastName ? " " + item.user.lastName : "")
              : item.farmerFirstName
              ? item.farmerFirstName +
                (item.farmerMiddleName ? " " + item.farmerMiddleName : "") +
                (item.farmerLastName ? " " + item.farmerLastName : "")
              : "N/A"
          }}
        </div>
      </template>

      <!-- Address of Production -->
      <template v-slot:item.farmAddress="{ item }">
        {{ decodeHtmlEntities(item.address) || "N/A" }}
      </template>
      <template v-slot:item.registrationNo="{ item }">
        {{ item.registrationNo || "N/A" }}
      </template>
      <template v-slot:item.region="{ item }">
        {{ item.region || "N/A" }}
      </template>
      <template v-slot:item.country="{ item }">
        {{ item.country || "N/A" }}
      </template>
      <template v-slot:item.farmLocation="{ item }">
        {{ item.mainLocation?.state || "N/A" }}
      </template>
      <template v-slot:item.technicianName="{ item }">
        {{ item.technician ? item.technician.fullName : "N/A" }}
      </template>
      <template v-slot:item.size="{ item }">
        {{ item.area ? convertSizeIntoBaseUnit(item.area) : "N/A" }}
      </template>
      <template v-slot:item.date="{ item }">
        {{ item.createdAt }}
      </template>
      <template v-slot:item.society="{ item }">
        {{ item?.factoryName ? item?.factoryName : "N/A" }}
      </template>
      
       <!-- Registered By & Date: two-line cell -->
      <template v-slot:item.regByDate="{ item }">
        <v-tooltip bottom color="black" max-width="350">
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="text-decoration-underline cursor-pointer primary--text font-weight-medium"
            >
              {{
                (item.technician && item.technician.firstName
                  ? `${item.technician.firstName} ${item.technician.lastName || ""}`
                  : "") ||
                (item.adminTechnician && item.adminTechnician.firstName
                  ? `${item.adminTechnician.firstName} ${item.adminTechnician.lastName || ""}`
                  : "") ||
                (item.user && item.user.firstName
                  ? `${item.user.firstName} ${item.user.lastName || ""}`
                  : "N/A")
              }}
            </div>
          </template>
          <div>
            <div>
              User Id:
              <b>{{
                (item.technician ? item.technician.id : "") ||
                (item.adminTechnician ? item.adminTechnician.id : "") ||
                (item.user ? item.user.id : "")
              }}</b>
            </div>
            <div>
              User Name:
              <b>{{
                (item.technician
                  ? `${item.technician.firstName || ""} ${item.technician.lastName || ""}`
                  : "") ||
                (item.adminTechnician
                  ? `${item.adminTechnician.firstName || ""} ${item.adminTechnician.lastName || ""}`
                  : "") ||
                (item.user
                  ? `${item.user.firstName || ""} ${item.user.lastName || ""}`
                  : "")
              }}</b>
            </div>
            <div>
              User Role: <b>{{ item.user?.user_role?.[0]?.name || "N/A" }}</b>
            </div>
          </div>
        </v-tooltip>
        <div class="text-caption grey--text text--darken-1 mt-1">
          {{ formatDate(item.createdAt) }}
        </div>
      </template>

      <template v-slot:item.status="{ item }">
        <div
          class="text-decoration-underline cursor-pointer primary--text"
          @click="viewFarm(item.id, 2)"
        >
          {{ $t('view') }}
        </div>
      </template>
      <template v-slot:item.action="{ item }">
        <div style="right: 10px; top: 10px">
          <v-menu location="start">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item :key="item.id" :disabled="!$can($permissions.can_update_farm.value)">
                <v-list-item-title
                  class="cursor-pointer"
                  @click="editFarm(item.id)"
                  :class="{'v-list-item--disabled': !$can($permissions.can_update_farm.value)}"
                  >{{ $t("edit") }}</v-list-item-title
                >
              </v-list-item>
              <v-list-item :disabled="!$can($permissions.can_delete_farm.value)">
                <v-list-item-title
                  class="cursor-pointer"
                  @click="changeFarmStatus(item.id)"
                  :class="{'v-list-item--disabled': !$can($permissions.can_delete_farm.value)}"
                  >{{ $t("delete") }}</v-list-item-title
                >
              </v-list-item>
              <!-- <v-list-item>
                <v-list-item-title>{{
                  $t("farm.generateReport")
                }}</v-list-item-title>
              </v-list-item> -->
            </v-list>
          </v-menu>
        </div>
      </template>
      <template v-slot:footer></template>
    </v-data-table>
    <confirm-box
      :title="confirmElements.title"
      :message="confirmElements.message"
      :cancelBtnText="confirmElements.cancelBtnText"
      :confirmBtnText="confirmElements.confirmBtnText"
      :confirmData="confirmElements.confirmData"
      :dialog="confirm"
      @cancel:action="confirm = false"
      @continue:action="confirmDeactivate"
    ></confirm-box>
          <v-dialog v-model="exportDialog" max-width="750px">
        <v-card>
          <v-toolbar color="#1a773d" dark flat height="60">
            <v-toolbar-title class="font-weight-bold text-h6">Export Farms List</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="exportDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pt-6 pb-2">
            <div class="subtitle-1 black--text font-weight-bold mb-5">
              Select the columns you wish to include in your CSV export.
            </div>

            <v-row>
              <v-col 
                cols="12" 
                sm="6" 
                v-for="col in exportColumns" 
                :key="col.value" 
                class="py-2"
              >
                     <div class="bordered-checkbox d-flex align-center justify-space-between pr-2">
                  <v-checkbox
                    v-model="selectedExportColumns"
                    :label="col.label"
                    :value="col.value"
                    color="#17c964"
                    hide-details
                    class="mt-0 pt-0"
                  ></v-checkbox>

                 <v-select
                    v-if="col.value === 'farmSize' && selectedExportColumns.includes('farmSize')"
                    v-model="selectedFarmSizeUnit"
                    :items="farmSizeUnitOptions"
                    outlined
                    dense
                    hide-details
                    style="max-width: 110px; height: 38px;"
                    class="mt-0 pt-0"
                  ></v-select>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <v-checkbox
              v-model="rememberExportPreferences"
              label="Remember my column preference for future export"
              color="#17c964"
              hide-details
              class="mt-0"
            ></v-checkbox>
          </v-card-text>

          <v-card-actions class="pb-6 pr-6 pt-4">
            <v-spacer></v-spacer>
            <v-btn 
              outlined 
              color="#17c964" 
              class="px-8 font-weight-bold" 
              height="45"
              @click="exportDialog = false"
            >
              CANCEL
            </v-btn>
            <v-btn 
              color="#17c964" 
              class="white--text px-8 ml-4 font-weight-bold" 
              height="45"
              depressed
              @click="generateCsv"
            >
              GENERATE CSV
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </div>
</template>
<script>
import { getCountries, getStates } from "country-state-picker";
import loadingMixin from "@/mixins/LoadingMixin";
import DownloadMixin from "@/mixins/DownloadMixin";
import MemberDataService from "@/_services/MemberDataService";
import { convertSizeIntoBaseUnit, decodeHtmlEntities } from "@/utils.js";
import _ from "lodash";
import ConfirmBox from "../../../components/ConfirmBox.vue";
import FarmService from "../../../_services/FarmService";
import BuyingStationService from "@/_services/BuyingStationService";
export default {
  components: { ConfirmBox },
  props: {
    headers: {
      type: Array,
    },
    toggleSelection: {
      type: Boolean,
    },
    farms: {
      type: Array,
    },
    totalFarms: {
      type: Number,
    },
    toggleFilterState: {
      type: Boolean,
      default: false,
    }
  },
  watch: {
    farms(newFarm) {
      this.localFarms = newFarm;
    },
  },
  computed: {
    totalPages() {
      return this.totalFarms < this.options.limit
        ? 1
        : Math.ceil(this.totalFarms / this.options.limit);
    },
    dateRangeText() {
      return this.options.date.join(" - ");
    },
    from() {
      return ((this.options.page - 1) * this.options.limit) + 1;
    },
    to() {
      const calculatedTo = this.options.page * this.options.limit;
      return calculatedTo > this.totalFarms ? this.totalFarms : calculatedTo;
    },
    farmCreatedFromOptions() {
      return [
        { value: "mobile", text: this.$t("farm.mobileApp") },
        { value: "admin", text: this.$t("farm.DDSReport") },
        { value: "farm_management", text: this.$t("farm.farmManagement") }
      ];
    },
  },
  data() {
    return {
      tableSelectKey: 0,
      localFarms: [],
      search: "",
      selectedFarms: [],
      options: {
        limit: 50,
        page: 1,
        searchPhrase: "",
        country: null,
        state: null,
        date: [],
        status: "",
        orderType: "DESC",
        areaUnitId: JSON.parse(localStorage.getItem("gs"))?.areaUnit.id,
        societyId: (() => { try { const s = localStorage.getItem('farmList_selectedSociety'); return s ? JSON.parse(s).id : null; } catch(e) { return null; } })(),
        farm_created_from: null,
      },
      countries: [],
      states: [],
      confirm: false,
      confirmElements: {
        title: this.$t("farm.deleteFarm"),
        message: this.$t("farm.confirmDeleteFarm"),
        confirmBtnText: this.$t("addEdituser.activateAct"),
        cancelBtnText: this.$t("cancel"),
        confirmData: {},
      },
      filterByDateMenu: false,
      societyList: [],
      selectedSociety: null,
        exportDialog: false,
      rememberExportPreferences: true,
      exportColumns: [
        { label: "Dimitra Farm ID", value: "dimitraFarmId" },
        { label: "Farm ID", value: "farmId" },
        { label: "Farm Name", value: "farmName" },
        { label: "Farmer Name", value: "farmerName" },
        { label: "Farm Address", value: "farmAddress" },
        { label: "Farm Location", value: "farmLocation" },
        { label: "Country", value: "country" },
        // { label: "Factory", value: "factory" },
        { label: "Reg. Date", value: "regDate" },
        { label: "Reg. By", value: "regBy" },
        { label: "Location Coordinates", value: "locationCoordinates" },
        { label: "Farm Size", value: "farmSize" },
        { label: "Geofence (Polygon)", value: "geofence" }
        // { label: "Reports", value: "reports" },
        // { label: "Local Database Verification", value: "localDbVerification" }
      ],
      selectedExportColumns: [
        "farmId", "farmName", "farmerName", "farmAddress", "country", "regDate", "regBy", "reports", "localDbVerification", "locationCoordinates", "farmSize", "geofence"
      ],
      selectedFarmSizeUnit: 'Acre',
      farmSizeUnitOptions: ['Acre', 'Hectare'],
    };
  },
  created() {
    if(this.$route.params?.id) {
      this.options = {...this.options, userId: this.$route.params?.id}
    }
    const savedPage = parseInt(localStorage.getItem("farmList_page"), 10);
    if (savedPage && savedPage > 0) {
      this.options.page = savedPage;
    }
    this.$emit("fetchFarmData", this.options);
    this.getCountry();
    this.getSocietyList();
  },
  methods: {
    bulkDelete() {
      if (!this.$can(this.$permissions.can_delete_farm.value)) {
        return;
      }
      if (!this.selectedFarms.length) {
        this.$notify({
          title: this.$t("farm.selectFarmsToDelete"),
          type: "warning",
        });
        return;
      }
      const count = this.selectedFarms.length;
      this.confirmElements.title = this.$t("farm.deleteFarm");
      this.confirmElements.message = this.$t("farm.confirmBulkDeleteFarm", { count });
      this.confirmElements.confirmBtnText = this.$t("yes");
      this.confirmElements.cancelBtnText = this.$t("cancel");
      this.confirmElements.confirmData = {
        farmIds: this.selectedFarms.map((f) => f.id),
      };
      this.confirm = true;
    },
     formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    },
    openExportDialog() {
      // Load saved preferences if the user checked "Remember" previously
      const savedPrefs = localStorage.getItem('farmExportColumns');
      if (savedPrefs) {
        this.selectedExportColumns = JSON.parse(savedPrefs);
      }
      this.exportDialog = true;
    },
      generateCsv() {
      // Save preferences if checkbox is checked
      if (this.rememberExportPreferences) {
        localStorage.setItem('farmExportColumns', JSON.stringify(this.selectedExportColumns));
           // Also remember the selected unit
        localStorage.setItem('farmExportSizeUnit', this.selectedFarmSizeUnit);
      } else {
        localStorage.removeItem('farmExportColumns');
        localStorage.removeItem('farmExportSizeUnit');
      }

      const csvUrl = MemberDataService.DOWNLOAD_FARMS_CSV_URL;
      const queryParams = new URLSearchParams({});
      
      if (this.selectedFarms && this.selectedFarms.length > 0) {
        const selectedFarmIds = this.selectedFarms.map(farm => farm.id).join(',');
        queryParams.append('farmIds', selectedFarmIds);
      }

      // Add selected columns so the backend knows what to include
      if (this.selectedExportColumns.length > 0) {
        queryParams.append('exportColumns', this.selectedExportColumns.join(','));
      }
      
         // SEND THE SELECTED UNIT TO THE BACKEND API
      if (this.selectedExportColumns.includes('farmSize')) {
        queryParams.append('farmSizeUnit', this.selectedFarmSizeUnit);
      }
      const url = `${csvUrl}?${queryParams}`;
      this.startLoading();
      this.downloadSourceFile(url, "text/csv").then(() => {
        this.stopLoading();
        this.exportDialog = false; // Close dialog on success
      });
    },
    getRegBy(item) {
      if (item.technician) {
        if(item.technician.firstName) {
          return item.technician.firstName + " " + item.technician.lastName;
        } else  {
          return item.technician.email || item.technician.mobile
        }
      } else if (item.adminTechnician) {
        if(item.adminTechnician.firstName) {
          return item.adminTechnician.firstName + " " + item.adminTechnician.lastName;
        } else if (item.adminTechnician.email) {
          return item.adminTechnician.email || item.adminTechnician.mobile;
        }
      } else if (item.user) {
        if(item.user.firstName) {
          return item.user.firstName + " " + item.user.lastName;
        } else if (item.user.email) {
          return item.user.email || item.user.mobile;
        }
      } else {
        return "";
      }
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    },
    handleFarmCreatedFromChange(farmCreatedFrom) {
      this.options.farm_created_from = farmCreatedFrom || null;
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    },
    handleClearFarmCreatedFrom() {
      this.options.farm_created_from = null;
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    },
    handleClearSociety() {
      this.selectedSociety = null;
      this.options.societyId = null;
      try { localStorage.removeItem('farmList_selectedSociety'); } catch (e) { /* ignore */ }
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    },
    getSocietyList() {
      BuyingStationService.listBuyingStations({
        page: 1,
        limit: 1000,
        search: "",
        order: "ASC"
      }).then(res => {
        if (res.success && res.data) {
          const arryList = res.data.rows || res.data || [];
          this.societyList = arryList.map(item => ({
            name: item.fullName || `${item.firstName || ''} ${item.middleName || ''} ${item.lastName || ''}`.trim(),
            id: item.id,
          }));
          // Restore selected society display object after list is ready
          try {
            const saved = localStorage.getItem('farmList_selectedSociety');
            if (saved) {
              const parsed = JSON.parse(saved);
              const match = this.societyList.find(s => s.id === parsed.id);
              if (match) {
                this.selectedSociety = match;
                // societyId is already in options from data() — re-emit to apply filter
                this.options.societyId = match.id;
                this.$emit("fetchFarmData", this.options);
              } else {
                // Society no longer exists — clean up stale entry
                localStorage.removeItem('farmList_selectedSociety');
                this.options.societyId = null;
              }
            }
          } catch (e) { /* ignore */ }
        }
      }).catch(error => {
        console.error("Error fetching society list:", error);
      });
    },
    handleSocietyChange(society) {
      if (society && society.id) {
        this.options.societyId = society.id;
        try { localStorage.setItem('farmList_selectedSociety', JSON.stringify({ id: society.id, name: society.name })); } catch (e) { /* ignore */ }
      } else {
        this.options.societyId = null;
        try { localStorage.removeItem('farmList_selectedSociety'); } catch (e) { /* ignore */ }
      }
      this.$emit("fetchFarmData", this.options);
    },
    decodeHtmlEntities,
    viewFarm(farmId, tabId) {
      this.$router.push({ name: "FarmInfo", params: { farmId, tabId } });
    },
    editFarm(farmId) {
      this.$router.push({ name: "EditFarms", params: { farmId } });
    },
    convertSizeIntoBaseUnit,
    handleSelect({ item, value }) {
      if (value) {
        const alreadySelected = this.selectedFarms.find((farm) => farm.id === item.id);
        if (!alreadySelected) {
          this.selectedFarms = [...this.selectedFarms, item];
        }
      } else {
        this.selectedFarms = this.selectedFarms.filter((farm) => farm.id !== item.id);
      }
    },
    handleSelectAll({ items, value }) {
      if (value) {
        const farmMap = new Map(this.selectedFarms.map((farm) => [farm.id, farm]));
        items.forEach((farm) => farmMap.set(farm.id, farm));
        this.selectedFarms = Array.from(farmMap.values());
      } else {
        const deselectedIds = items.map((farm) => farm.id);
        this.selectedFarms = this.selectedFarms.filter(
          (farm) => !deselectedIds.includes(farm.id)
        );
      }
    },
    getCountry() {
      this.countries = getCountries();
    },
    getState(val) {
      this.states = getStates(val.code);
      this.options.country = val.name;
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    },
    getStateData(val) {
      this.options.state = val;
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    },
    handleChangeDate() {
      this.$emit("fetchFarmData", this.options);
      this.filterByDateMenu = false
    },
    handleClearDate() {
      this.options.date = [];
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    },
    handleClearCountry() {
      this.options.country = null;
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    },
    handleClearState() {
      this.options.state = null;
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    },
    handlePageChange(page) {
      this.options.page = page;
      localStorage.setItem("farmList_page", page);
      this.$emit("fetchFarmData", this.options);
    },
    _resetPage() {
      this.options.page = 1;
      localStorage.setItem("farmList_page", 1);
    },
    handleSearchByPharse: _.debounce(function () {
      this.options.searchPhrase = this.search;
      this._resetPage();
      this.$emit("fetchFarmData", this.options);
    }, 1000),
    downloadCsv() {
      const csvUrl = MemberDataService.DOWNLOAD_FARMS_CSV_URL;
      const queryParams = new URLSearchParams(this.options);
      
      if (this.selectedFarms && this.selectedFarms.length > 0) {
        const selectedFarmIds = this.selectedFarms.map(farm => farm.id).join(',');
        queryParams.append('farmIds', selectedFarmIds);
      }
      
      const url = `${csvUrl}?${queryParams}`;
      this.startLoading();
      this.downloadSourceFile(url, "text/csv").then(() => {
        this.stopLoading();
      });
    },
    changeFarmStatus(id) {
      this.confirmElements.title = this.$t("farm.deleteFarm");
      (this.confirmElements.message = this.$t("farm.confirmDeleteFarm")),
        (this.confirmElements.confirmBtnText = this.$t("yes"));
      this.confirmElements.confirmData = { farmId: id };
      this.confirm = true;
    },
    async confirmDeactivate(action, confirmData) {
      if (action) {
        await this.startLoading();
        const isBulk =
          Array.isArray(confirmData.farmIds) && confirmData.farmIds.length > 0;
        const request = isBulk
          ? FarmService.bulkDeactiveFarms(confirmData.farmIds)
          : FarmService.deactiveFarm(confirmData.farmId);
        request
          .then(async (res) => {
            if (res.success) {
              await this.stopLoading();
              this.$emit("fetchFarmData", this.options);
              this.selectedFarms = [];
              this.tableSelectKey += 1;
              this.confirm = false;
              this.$notify({
                title: isBulk
                  ? this.$t("farm.bulkDeleted", {
                      count: confirmData.farmIds.length,
                    })
                  : this.$t("farm.deleted"),
                type: "success",
              });
            } else {
              await this.stopLoading();
              this.confirm = false;
              this.$notify({
                title: this.$t("farm.notDeleted"),
                type: "error",
              });
            }
          })
          .catch(async () => {
            await this.stopLoading();
            this.confirm = false;
          });
      } else {
        this.confirm = false;
      }
    },
  },
  mixins: [loadingMixin, DownloadMixin],
};
</script>

<style scoped>
/* Mobile card layout */
::v-deep .v-data-table__mobile-row {
  min-height: 36px;
  padding: 4px 16px;
}

::v-deep .v-data-table__mobile-row__header {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  min-width: 130px;
  padding-right: 12px;
}

::v-deep .v-data-table__mobile-row__cell {
  word-break: break-word;
}

::v-deep .v-data-table__mobile-table-row {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  margin: 8px;
  display: block;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* Ensure table scrolls horizontally on medium screens before card breakpoint */
::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

::v-deep .v-pagination__item,
::v-deep .v-pagination__navigation {
  box-shadow: none;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 50% !important;
}
</style>
